import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useMutation } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/components/ui/page-header'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { supabase } from '@/lib/supabase'
import { useHousehold } from '@/context/HouseholdContext'
import { useUpdateLanguage } from '@/hooks/useLanguage'
import { useT } from '@/i18n/I18nProvider'
import { LANGS } from '@/i18n/config'

// Configurações → Perfil: nome do perfil + idioma da interface.
export function HouseholdSettingsPage() {
  const { household, householdId, language, refresh, loading } = useHousehold()
  const { t } = useT()
  const setLang = useUpdateLanguage()
  const [name, setName] = useState('')

  useEffect(() => {
    setName(household?.name ?? '')
  }, [household?.name])

  const save = useMutation({
    mutationFn: async (newName: string) => {
      const { error } = await supabase.from('households').update({ name: newName }).eq('id', householdId)
      if (error) throw error
    },
    onSuccess: async () => {
      await refresh()
      toast.success(t('common.save'))
    },
    onError: (e) => toast.error((e as Error).message),
  })

  const trimmed = name.trim()
  const dirty = trimmed !== (household?.name ?? '')

  async function changeLang(code: string) {
    if (code === language) return
    try {
      await setLang.mutateAsync(code)
    } catch (e) {
      toast.error((e as Error).message)
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader title={t('settings.profile')} />

      <div className="max-w-sm space-y-4">
        <Card className="py-0">
          <CardContent className="space-y-3 p-5">
            <h2 className="font-medium">{t('profile.name')}</h2>
            <div className="space-y-2">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading || save.isPending}
              />
              <Button onClick={() => save.mutate(trimmed)} disabled={!dirty || !trimmed || save.isPending}>
                {save.isPending ? t('common.saving') : t('common.save')}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="py-0">
          <CardContent className="space-y-3 p-5">
            <h2 className="font-medium">{t('settings.language')}</h2>
            <Select value={language} onValueChange={changeLang} disabled={loading || setLang.isPending}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGS.map((l) => (
                  <SelectItem key={l.code} value={l.code}>
                    {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
